import { EpisodeState } from "@/api/episodes-types";
import { AssigneeFilter } from "@/components/ui/filters/AssigneeFilter";
import {
  getAssigneeValue,
  getTextValue,
  handleAssigneeChange,
  handleCategoryChange,
  handleMultiSelectChange,
  handleSingleSelectChange,
  handleTextChange,
  parseFiltersByField,
} from "@/components/ui/filters/filterHandlers";
import {
  MultiSelectFilter,
  SelectOption,
} from "@/components/ui/filters/MultiSelectFilter";
import { SingleSelectFilter } from "@/components/ui/filters/SingleSelectFilter";
import { TextFilter } from "@/components/ui/filters/TextFilter";
import { EpisodeStateChip } from "@/components/ui/state-chips/EpisodeStateChip";
import { useTags } from "@/queries/episodes/use-tags";
import {
  categoryOptions,
  episodeStateOptions,
  genderOptions,
  modelOptions,
  storyTypeOptions,
  subcategoryOptions,
} from "@/utils/datagrid-utils";
import { DeleteOutline } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { GridFilterItem } from "@mui/x-data-grid";
import { useMemo } from "react";

type EpisodeFiltersProps = {
  items: GridFilterItem[];
  onChange: (items: GridFilterItem[]) => void;
};

export function EpisodeFilters({ items, onChange }: EpisodeFiltersProps) {
  const { data: tags } = useTags();

  const tagOptions: SelectOption[] = useMemo(
    () => (tags ?? []).map((t) => ({ value: t, label: t })),
    [tags],
  );

  const filtersByField = useMemo(() => parseFiltersByField(items), [items]);

  return (
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
      <TextFilter
        field="id"
        label="ID"
        value={getTextValue("id", filtersByField)}
        onChange={(field, val) => handleTextChange(field, val, items, onChange)}
      />
      <MultiSelectFilter
        field="story"
        label="Story Type"
        options={storyTypeOptions}
        value={filtersByField["story"] ?? []}
        onChange={(field, values) =>
          handleMultiSelectChange(field, values, items, onChange)
        }
      />
      <SingleSelectFilter
        field="category"
        label="Category"
        options={categoryOptions}
        value={getTextValue("category", filtersByField)}
        onChange={(field, val) =>
          handleCategoryChange(field, val, items, onChange)
        }
      />
      <SingleSelectFilter
        field="subcategory"
        label="Subcategory"
        disabled={!getTextValue("category", filtersByField)}
        options={subcategoryOptions(getTextValue("category", filtersByField))}
        value={getTextValue("subcategory", filtersByField)}
        onChange={(field, val) =>
          handleSingleSelectChange(field, val, items, onChange)
        }
      />
      <SingleSelectFilter
        field="gender"
        label="Gender"
        options={genderOptions}
        value={getTextValue("gender", filtersByField)}
        onChange={(field, val) =>
          handleSingleSelectChange(field, val, items, onChange)
        }
      />
      <SingleSelectFilter
        field="modelName"
        label="LLM Model Name"
        options={modelOptions}
        value={getTextValue("modelName", filtersByField)}
        onChange={(field, val) =>
          handleSingleSelectChange(field, val, items, onChange)
        }
      />
      <MultiSelectFilter
        field="state"
        label="State"
        options={episodeStateOptions}
        value={filtersByField["state"] ?? []}
        onChange={(field, values) =>
          handleMultiSelectChange(field, values, items, onChange)
        }
        renderChip={(stateValue, onDelete) => (
          <EpisodeStateChip
            state={stateValue as EpisodeState}
            onDelete={onDelete}
          />
        )}
      />
      <AssigneeFilter
        field="visualsAssignee.username"
        label="Assignee"
        value={getAssigneeValue("visualsAssignee.username", items)}
        onChange={(field, val) =>
          handleAssigneeChange(field, val, items, onChange)
        }
      />
      <TextFilter
        field="prompt"
        label="Text Prompt"
        value={getTextValue("prompt", filtersByField)}
        onChange={(field, val) => handleTextChange(field, val, items, onChange)}
      />
      <TextFilter
        field="imagePrompt"
        label="Image Prompt"
        value={getTextValue("imagePrompt", filtersByField)}
        onChange={(field, val) => handleTextChange(field, val, items, onChange)}
      />
      <MultiSelectFilter
        field="tags"
        label="Tags"
        options={tagOptions}
        value={filtersByField["tags"] ?? []}
        onChange={(field, values) =>
          handleMultiSelectChange(field, values, items, onChange)
        }
      />
      <SingleSelectFilter
        field="hasManualEditImages"
        label=" Needs Manual Edit"
        options={[
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ]}
        value={getTextValue("hasManualEditImages", filtersByField)}
        onChange={(field, val) =>
          handleSingleSelectChange(field, val, items, onChange)
        }
      />
      <IconButton disabled={items.length === 0} onClick={() => onChange([])}>
        <DeleteOutline />
      </IconButton>
    </Stack>
  );
}
